import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// ============================================================================
// COMPONENTES LOCAIS
// ============================================================================
import { Layout } from 'components/ds';
import ExternalPageLoader from './ExternalPageLoader';
import FilteredModules from './FilteredModules';

// ============================================================================
// HOOKS E SERVIÇOS
// ============================================================================
import { useModulesContext } from '../services/providers/ModulesProvider';

// ============================================================================
// ESTILOS
// ============================================================================
import { GlobalStyle, Provider } from '../components/pages/Pages.styles';
import { DynamicPageContent, LoadingPlaceholder } from './DynamicPage.styles';

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Função auxiliar para fazer match de rotas com parâmetros
 */
const matchRoute = (routePattern: string, actualPath: string): boolean => {
  const pattern = routePattern.startsWith('/') ? routePattern.substring(1) : routePattern;
  const path = actualPath.startsWith('/') ? actualPath.substring(1) : actualPath;
  
  const patternSegments = pattern.split('/');
  const pathSegments = path.split('/');
  
  if (patternSegments.length !== pathSegments.length) {
    return false;
  }
  
  for (let i = 0; i < patternSegments.length; i++) {
    const patternSegment = patternSegments[i];
    const pathSegment = pathSegments[i];
    
    if (patternSegment.startsWith(':')) {
      continue;
    }
    
    if (patternSegment !== pathSegment) {
      return false;
    }
  }
  
  return true;
};

// ============================================================================
// COMPONENTE
// ============================================================================

const getNormalizedComponentPage = (componentPage?: string): string | null => {
  if (typeof componentPage !== 'string' || !componentPage.trim()) {
    return null;
  }

  return componentPage.startsWith('/')
    ? componentPage.substring(1)
    : componentPage;
};

const DynamicPage: React.FC = () => {
  const location = useLocation();
  const params = useParams();
  const modules = useModulesContext();
  const pageName = location.pathname.substring(1);
  const [isLoading, setIsLoading] = useState(true);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    setIsLoading(true);
  }, [location.pathname]);

  // ============================================================================
  // LÓGICA DE BUSCA DE MÓDULO
  // ============================================================================

  // Primeiro, tenta fazer match exato
  let pageModule = modules.find(
    (module) => {
      const modulePage = getNormalizedComponentPage(module.componentPage);
      if (!modulePage) {
        return false;
      }
      return modulePage === pageName;
    }
  );

  // Se não encontrou match exato, tenta fazer match com parâmetros
  if (!pageModule) {
    const updatedPageName = Object.entries(params).reduce(
      (acc, [key, value]) => acc.replace(
        new RegExp(`/${String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=/|$)`, 'g'),
        `/:${key}`
      ),
      pageName,
    );

    pageModule = modules.find(
      (module) => {
        const modulePage = getNormalizedComponentPage(module.componentPage);
        if (!modulePage) {
          return false;
        }
        return modulePage === updatedPageName;
      }
    );

    // Se ainda não encontrou, tenta fazer match usando a função de matching
    if (!pageModule) {
      pageModule = modules.find(
        (module) => {
          if (!module.componentPage || module.componentPosition !== 'page') {
            return false;
          }
          return matchRoute(module.componentPage, location.pathname);
        }
      );
    }
  }

  // ============================================================================
  // TRATAMENTO DE ERRO - MÓDULO NÃO ENCONTRADO
  // ============================================================================

  if (!pageModule) {
    return (
      <div>
        <h2>Página não encontrada</h2>
        <p>Não foi possível encontrar um módulo para: {pageName}</p>
        <p>Parâmetros da rota: {JSON.stringify(params)}</p>
        <details>
          <summary>Módulos disponíveis (clique para expandir)</summary>
          <pre>{JSON.stringify(modules.filter(m => m.componentPosition === 'page'), null, 2)}</pre>
        </details>
      </div>
    );
  }

  // ============================================================================
  // RENDERIZAÇÃO
  // ============================================================================

  const mergedProps = {
    ...(pageModule.props || {}),
  };

  const isPublicPage = pageModule.public;
  const headerFooterPage = isPublicPage ? 'welcome' : undefined;

  return (
    <>
      <GlobalStyle />
      <FilteredModules componentName="provider">
        <Provider backend={HTML5Backend}>
          <Layout className="pwa-layout">
            <FilteredModules componentName="header" componentPage={headerFooterPage} />
            <DynamicPageContent>
              {isLoading && (
                <LoadingPlaceholder>
                  <Spin size="large" />
                </LoadingPlaceholder>
              )}
              <ExternalPageLoader
                key={`${pageModule.id}-${location.pathname}`}
                moduleUrl={pageModule.moduleUrl}
                containerName={pageModule.containerName}
                componentPath={pageModule.componentPath}
                props={mergedProps}
                onLoadingChange={setIsLoading}
              />
            </DynamicPageContent>
            <FilteredModules componentName="footer" componentPage={headerFooterPage} />
          </Layout>
        </Provider>
      </FilteredModules>
    </>
  );
};

export default DynamicPage;
